var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ADD_MODEL from 'api/mutations/ADD_MODEL';
import DUPLICATE_MODEL from 'api/mutations/DUPLICATE_MODEL';
import SET_SPECIFICATION_STATE_TO_PRODUCTION from 'api/mutations/SET_SPECIFICATION_STATE_TO_PRODUCTION';
import SYNC_SPECIFICATION_VARIANTS from 'api/mutations/SYNC_SPECIFICATION_VARIANTS';
import GET_MODEL_SPECIFICATION_PROBLEMS from 'api/queries/GET_MODEL_SPECIFICATION_PROBLEMS';
import GET_SPECIFICATION_HEADER from 'api/queries/GET_SPECIFICATION_HEADER';
import GET_SPECIFICATION_SUMMARY from 'api/queries/GET_SPECIFICATION_SUMMARY';
import client from '../api/environment';
const initialState = {
    item: null,
    brandNewSpec: false,
    prevPath: '/specifications',
    creatorActiveTab: null,
    stepValidationRequestId: 0,
    status: {
        fetch: 'idle',
        fetchSummary: 'idle',
        fetchProblems: 'idle',
        add: 'idle',
        duplicate: 'idle',
        setToProduction: 'idle',
    },
    errors: {
        fetch: 'idle',
        fetchSummary: 'idle',
        fetchProblems: 'idle',
        add: 'idle',
        duplicate: 'idle',
        setToProduction: 'idle',
    },
    refetchFlags: {
        fetch: false,
        fetchProblems: false,
        add: false,
        duplicate: false,
        setToProduction: false,
    },
};
export const fetchSpecificationDetails = createAsyncThunk('specificationDetails/fetchSpecificationDetails', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ specificationId }, { rejectWithValue }) {
    try {
        const data = yield client.query({
            query: GET_SPECIFICATION_HEADER,
            variables: { modelSpecificationId: specificationId },
        });
        return data.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationSummaryDetails = createAsyncThunk('specificationDetails/fetchSpecificationSummaryDetails', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ specificationId }, { rejectWithValue }) {
    try {
        const data = yield client.query({
            query: GET_SPECIFICATION_SUMMARY,
            variables: { modelSpecificationId: specificationId },
        });
        return data.data.node;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const fetchSpecificationProblems = createAsyncThunk('specificationDetails/fetchSpecificationProblems', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ specificationId }, { rejectWithValue }) {
    try {
        const specifiactionData = yield client.query({
            query: GET_MODEL_SPECIFICATION_PROBLEMS,
            variables: { modelSpecificationId: specificationId },
            fetchPolicy: 'network-only',
        });
        return Object.assign({}, specifiactionData.data.node);
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const syncSpecificationVariants = createAsyncThunk('specificationDetails/syncSpecificationVariants', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ specificationId }, { rejectWithValue }) {
    try {
        const response = yield client.mutate({
            mutation: SYNC_SPECIFICATION_VARIANTS,
            variables: { specificationId },
        });
        return response.data.syncSpecificationVariants;
    }
    catch (error) {
        return rejectWithValue(error.message);
    }
}));
export const addSpecification = createAsyncThunk('specificationDetails/addSpecification', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ folderId }, { rejectWithValue }) {
    try {
        const specifiactionData = yield client.mutate({
            mutation: ADD_MODEL,
            variables: { folderId },
        });
        return specifiactionData.data.addModelSpecification.modelSpecification;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const duplicateSpecification = createAsyncThunk('specificationDetails/duplicateSpecification', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ modelSpecificationId, newSpecificationItemCode, }, { rejectWithValue }) {
    try {
        const specifiactionData = yield client.mutate({
            mutation: DUPLICATE_MODEL,
            variables: { modelSpecificationId, newSpecificationItemCode },
        });
        return specifiactionData.data.copyModelSpecification.modelSpecification.id;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const setSpecificationStateToProduction = createAsyncThunk('specificationDetails/setSpecificationStateToProduction', (_a, _b) => __awaiter(void 0, [_a, _b], void 0, function* ({ modelSpecificationId }, { rejectWithValue }) {
    try {
        const specifiactionData = yield client.mutate({
            mutation: SET_SPECIFICATION_STATE_TO_PRODUCTION,
            variables: { modelSpecificationId },
        });
        return specifiactionData.data.setModelSpecificationStateToProduction.modelSpecification;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const specificationDetailsSlice = createSlice({
    name: 'specificationDetails',
    initialState,
    reducers: {
        resetSpecificationDetails() {
            return initialState;
        },
        refetchProblems(state) {
            state.refetchFlags.fetchProblems = true;
        },
        setPrevPath: (state, action) => {
            const { path } = action.payload;
            state.prevPath = path;
        },
        setBrandNewSpecState: (state, action) => {
            state.brandNewSpec = action.payload;
        },
        updateSpecification: (state, action) => {
            state.item[action.payload.name] = action.payload.value;
        },
        updateSpecificationTeamMembers: (state, action) => {
            state.item.specificationTeamMembersNode[action.payload.name] = action.payload.value;
        },
        addPicture: (state, action) => {
            state.item.image = action.payload;
        },
        removePicture: (state) => {
            state.item.image = null;
        },
        setCreatorActiveTab: (state, action) => {
            state.creatorActiveTab = action.payload;
        },
        requestStepValidation: (state) => {
            state.stepValidationRequestId += 1;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpecificationDetails.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchSpecificationDetails.fulfilled, (state, action) => {
            state.item = Object.assign(Object.assign({}, state.item), action.payload);
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchSpecificationDetails.rejected, (state) => {
            state.status.fetch = 'failed';
            state.errors.fetch = 'Failed to fetch specification details';
        })
            .addCase(fetchSpecificationSummaryDetails.pending, (state) => {
            state.status.fetchSummary = 'loading';
        })
            .addCase(fetchSpecificationSummaryDetails.fulfilled, (state, action) => {
            state.item = Object.assign(Object.assign({}, state.item), action.payload);
            state.status.fetchSummary = 'succeeded';
        })
            .addCase(fetchSpecificationSummaryDetails.rejected, (state) => {
            state.status.fetchSummary = 'failed';
            state.errors.fetchSummary = 'Failed to fetch specification summary';
        })
            .addCase(fetchSpecificationProblems.pending, (state) => {
            state.status.fetchProblems = 'loading';
            state.refetchFlags.fetchProblems = false;
        })
            .addCase(fetchSpecificationProblems.fulfilled, (state, action) => {
            state.item = Object.assign(Object.assign({}, state.item), action.payload);
            state.status.fetchProblems = 'succeeded';
        })
            .addCase(fetchSpecificationProblems.rejected, (state) => {
            state.status.fetchProblems = 'failed';
            state.errors.fetchProblems = 'Failed to fetch specification details';
        })
            .addCase(addSpecification.pending, (state) => {
            state.status.add = 'loading';
        })
            .addCase(addSpecification.fulfilled, (state, action) => {
            state.item = action.payload;
            state.brandNewSpec = true;
            state.status.add = 'succeeded';
        })
            .addCase(addSpecification.rejected, (state, action) => {
            state.status.add = 'failed';
            state.errors.add = action.error.message || 'Failed to add specification';
        })
            .addCase(duplicateSpecification.pending, (state) => {
            state.status.duplicate = 'loading';
        })
            .addCase(duplicateSpecification.fulfilled, (state) => {
            state.status.duplicate = 'succeeded';
        })
            .addCase(duplicateSpecification.rejected, (state, action) => {
            state.status.duplicate = 'failed';
            state.errors.duplicate = action.error.message || 'Failed to add specification';
        })
            .addCase(setSpecificationStateToProduction.pending, (state) => {
            state.status.setToProduction = 'loading';
            state.refetchFlags.setToProduction = false;
        })
            .addCase(setSpecificationStateToProduction.fulfilled, (state, action) => {
            state.item = Object.assign(Object.assign({}, state.item), action.payload);
            state.status.setToProduction = 'succeeded';
        })
            .addCase(setSpecificationStateToProduction.rejected, (state) => {
            state.status.setToProduction = 'failed';
            state.errors.setToProduction = 'Failed to fetch specification details';
        });
    },
});
export const { resetSpecificationDetails, refetchProblems, setPrevPath, setBrandNewSpecState, updateSpecification, updateSpecificationTeamMembers, addPicture, removePicture, setCreatorActiveTab, requestStepValidation, } = specificationDetailsSlice.actions;
export const selectSpecificationId = (state) => { var _a; return (_a = state.specificationDetails.item) === null || _a === void 0 ? void 0 : _a.id; };
export const selectSpecificationDetailsStatus = (state) => state.specificationDetails.status;
export const selectCreatorActiveTab = (state) => state.specificationDetails.creatorActiveTab;
export const selectStepValidationRequestId = (state) => state.specificationDetails.stepValidationRequestId;
export default specificationDetailsSlice.reducer;
